import {
  getMessaging,
  getToken,
  onMessage,
  isSupported,
} from "firebase/messaging";
import { getFunctions, httpsCallable } from "firebase/functions";
import {
  doc,
  setDoc,
  updateDoc,
  getDoc,
  arrayRemove,
  serverTimestamp,
  arrayUnion,
  deleteField,
  addDoc,
  collection,
} from "firebase/firestore";
import { app, db, auth } from "./firebase";

// --------------------------------------
// client-side event logging (for debugging on devices without devtools)
// writes a small document to Firestore; used when notifications stop working
export const logClientEvent = async (event, detail = {}) => {
  try {
    const logsRef = collection(db, "clientLogs");
    await addDoc(logsRef, {
      event,
      detail,
      timestamp: serverTimestamp(),
    });
  } catch (err) {
    console.warn("logClientEvent failed:", err);
  }
};
// --------------------------------------

let messagingInstance = null;

export const isMessagingSupported = async () => {
  try {
    return await isSupported();
  } catch (error) {
    console.warn("Messaging support check failed:", error);
    return false;
  }
};

export const initializeMessaging = async (onForegroundMessage) => {
  if (!(await isMessagingSupported())) return null;

  if (!messagingInstance) {
    messagingInstance = getMessaging(app);
  }

  if (onForegroundMessage) {
    onMessage(messagingInstance, onForegroundMessage);
  }

  return messagingInstance;
};

export const registerServiceWorkerAndGetToken = async () => {
  if (!(await isMessagingSupported())) {
    await logClientEvent("register-sw", { stage: "unsupported" });
    throw new Error("Push messaging is not supported in this browser.");
  }

  if (!("serviceWorker" in navigator)) {
    await logClientEvent("register-sw", { stage: "no-service-worker" });
    throw new Error("Service workers are not supported in this browser.");
  }

  if (typeof window !== "undefined" && !window.isSecureContext) {
    throw new Error("Push notifications require HTTPS.");
  }

  const publicUrl = process.env.PUBLIC_URL || "";
  // append version query param to bust cache when we update the SW
  const swVersion = process.env.REACT_APP_FCM_SW_VERSION || "1";
  const swUrl = `${publicUrl}/firebase-messaging-sw.js?v=${swVersion}`;

  console.log("Registering firebase-messaging-sw.js service worker...");
  console.log("Service worker URL:", swUrl);

  const registrations = await navigator.serviceWorker.getRegistrations();
  console.log("Existing service worker registrations:", registrations);
  await logClientEvent("register-sw", { stage: "check-registrations", count: registrations.length });

  // look for a registration that already matches the current version query param
  const messagingRegistrations = registrations.filter((registration) => {
    const scriptUrl =
      registration?.active?.scriptURL ||
      registration?.waiting?.scriptURL ||
      registration?.installing?.scriptURL ||
      "";
    console.log("Checking service worker script URL:", scriptUrl);
    // prefer an exact-match including the version string so we know it's up-to-date
    return scriptUrl.includes(`firebase-messaging-sw.js?v=${swVersion}`);
  });

  let registration;
  if (messagingRegistrations.length > 0) {
    registration = messagingRegistrations[0];
    console.log(
      "Using existing firebase-messaging service worker registration",
    );
    await logClientEvent("register-sw", { stage: "using-existing" });
    // make sure it's the latest copy (this will fetch the script again if changed)
    try {
      await registration.update();
      console.log("Service worker update() called");
      await logClientEvent("register-sw", { stage: "update-called" });
    } catch (err) {
      console.warn("Failed to update existing service worker:", err);
      await logClientEvent("register-sw", { stage: "update-failed", error: err.message });
    }
  } else {
    console.log("Registering new firebase-messaging service worker...");
    await logClientEvent("register-sw", { stage: "register-new", url: swUrl });
    registration = await navigator.serviceWorker.register(swUrl);
    console.log("Firebase messaging service worker registered:", registration);
    await logClientEvent("register-sw", { stage: "registered", scope: registration.scope });
  }

  if (!registration || !registration.pushManager) {
    await logClientEvent("register-sw", { stage: "no-push-manager" });
    throw new Error(
      "PushManager is not available. Ensure the service worker is registered and active.",
    );
  }

  console.log("Service worker is ready and PushManager is available.");

  const messaging = await initializeMessaging();
  const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY;

  if (!vapidKey) {
    throw new Error("Missing REACT_APP_FIREBASE_VAPID_KEY");
  }

  const token = await getToken(messaging, {
    vapidKey,
    serviceWorkerRegistration: registration,
  });
  await logClientEvent("register-sw", { stage: "got-token", token: token || null });

  // if the token changed from last visit, persist the new value and save it
  try {
    const prev = localStorage.getItem("gr5_fcm_token");
    if (token && token !== prev) {
      localStorage.setItem("gr5_fcm_token", token);
      // this file already defines saveFCMToken below
      await saveFCMToken(token);
      console.log("FCM token refreshed and saved:", token);
    }
  } catch (err) {
    console.warn("Error checking/saving FCM token:", err);
  }

  return { token };
};

export const saveFCMToken = async (token) => {
  if (!token) return;

  const uid = auth.currentUser?.uid || null;
  if (!uid) {
    // User not logged in yet - store token locally for later sync
    try {
      const savedTokens = JSON.parse(
        localStorage.getItem("gr5_pending_fcm_token") || "[]"
      );
      if (!savedTokens.includes(token)) {
        savedTokens.push(token);
        localStorage.setItem("gr5_pending_fcm_token", JSON.stringify(savedTokens));
      }
    } catch (error) {
      console.warn(
        "Failed to store pending FCM token locally:",
        error.message
      );
    }
    console.log(
      "User not logged in yet. FCM token stored locally and will be synced after login."
    );
    return;
  }

  const tokenRef = doc(db, "userTokens", uid);
  const userAgent = navigator.userAgent || "";
  const snapshot = await getDoc(tokenRef);
  const existing = snapshot.exists() ? snapshot.data() : {};
  const existingMeta = existing.tokensMeta || {};

  const nextMeta = {};
  Object.entries(existingMeta).forEach(([existingToken, meta]) => {
    if (meta?.userAgent && meta.userAgent === userAgent) return;
    nextMeta[existingToken] = meta;
  });

  nextMeta[token] = {
    userAgent,
    lastSeenAt: serverTimestamp(),
  };

  const nextTokens = Object.keys(nextMeta);

  await setDoc(
    tokenRef,
    {
      uid,
      tokens: nextTokens,
      tokensMeta: nextMeta,
      userAgent,
      createdAt: existing.createdAt || serverTimestamp(),
      lastSeenAt: serverTimestamp(),
    },
    { merge: true },
  );
};

export const removeFCMToken = async (token) => {
  if (!token) return;
  const uid = auth.currentUser?.uid || null;
  if (!uid) return;
  const tokenRef = doc(db, "userTokens", uid);
  await updateDoc(tokenRef, {
    tokens: arrayRemove(token),
    lastSeenAt: serverTimestamp(),
    [`tokensMeta.${token}`]: deleteField(),
  });
};

export const syncPendingFCMTokens = async () => {
  try {
    const uid = auth.currentUser?.uid || null;
    if (!uid) return;

    const pendingTokens = JSON.parse(
      localStorage.getItem("gr5_pending_fcm_token") || "[]"
    );
    if (pendingTokens.length === 0) return;

    // Save all pending tokens
    for (const token of pendingTokens) {
      await saveFCMToken(token);
    }

    // Clear pending tokens after successful sync
    localStorage.removeItem("gr5_pending_fcm_token");
    console.log(
      `Successfully synced ${pendingTokens.length} pending FCM token(s)`
    );
  } catch (error) {
    console.error("Failed to sync pending FCM tokens:", error);
  }
};

export const getAllUserTokens = async () => [];

export const requestNotificationPermission = async () => {
  if (!("Notification" in window)) {
    return { granted: false, reason: "unsupported" };
  }

  if (Notification.permission === "granted") {
    return { granted: true };
  }

  if (Notification.permission === "denied") {
    return { granted: false, reason: "denied" };
  }

  const permission = await Notification.requestPermission();
  return { granted: permission === "granted", reason: permission };
};

export const sendHikeNotification = async ({
  hikeId,
  hikeName,
  message,
  force = false,
}) => {
  try {
    const functions = getFunctions(app);
    const callable = httpsCallable(functions, "sendHikeNotification");
    const result = await callable({ hikeId, hikeName, message, force });
    return { success: true, ...result.data };
  } catch (error) {
    console.error("Error sending hike notification:", error);
    return { success: false, error: error.message };
  }
};
